// src/lib/admob.ts
//
// Per-slot AdMob fallback. The reader prefers proprietary KleverBook ads
// served by /api/d2p/kleverbook/ads/serve; when the server returns null
// (empty inventory) we fall through to an AdMob interstitial. This is
// the interim revenue plan while the proprietary ad pool fills up.
//
// AdMob is native-only — on web/PWA we no-op so `serveAd → null → ...`
// just shows nothing. The Capacitor plugin needs ad unit IDs configured
// per platform; placeholder test IDs are used unless real ones are set
// via env vars at build time.

import { Capacitor } from '@capacitor/core';

let initialised = false;

// IMPORTANT: read env vars with plain dot access so CRA's webpack
// DefinePlugin can substitute them at build time. Optional chaining
// (process.env?.X) silently defeats the replacement and the value ends
// up undefined in the bundled webview — same bug that hid c5m.world
// behind localhost in apis/index.ts. Test ad unit IDs are kept as the
// fallback so a dev build always shows *something* in ad slots.
export const ADMOB_FALLBACK_ENABLED =
  process.env.REACT_APP_ADMOB_FALLBACK_ENABLED !== 'false';

const TEST_INTERSTITIAL_ID = {
  android: 'ca-app-pub-3940256099942544/1033173712',
  ios: 'ca-app-pub-3940256099942544/4411468910',
};

function pickInterstitialId(platform: string): string {
  if (platform === 'android') {
    const real = process.env.REACT_APP_ADMOB_INTERSTITIAL_ANDROID;
    return real && real.trim() ? real : TEST_INTERSTITIAL_ID.android;
  }
  if (platform === 'ios') {
    const real = process.env.REACT_APP_ADMOB_INTERSTITIAL_IOS;
    return real && real.trim() ? real : TEST_INTERSTITIAL_ID.ios;
  }
  return '';
}

async function getAdMob() {
  // Lazy import — the plugin module crashes at import time if Capacitor
  // hasn't bootstrapped on web, so we only require it when actually used.
  const mod = await import('@capacitor-community/admob');
  // Do NOT `return mod.AdMob` directly: the Capacitor plugin proxy is treated
  // as a thenable by the async machinery, which calls `.then()` on it and
  // forwards it to native as a non-existent method ("AdMob.then() is not
  // implemented on android"). Returning it wrapped in an object avoids the
  // promise-resolution unwrap of the proxy.
  return { AdMob: mod.AdMob };
}

export async function ensureAdMobInitialised(): Promise<boolean> {
  if (!ADMOB_FALLBACK_ENABLED) return false;
  if (!Capacitor.isNativePlatform()) return false;
  if (initialised) return true;
  try {
    const { AdMob } = await getAdMob();
    await AdMob.initialize({
      initializeForTesting: process.env.NODE_ENV !== 'production',
    });
    initialised = true;
    return true;
  } catch (e) {
    console.log('AdMob init error:', e);
    return false;
  }
}

export async function showInterstitialFallback(): Promise<boolean> {
  const ok = await ensureAdMobInitialised();
  if (!ok) return false;
  try {
    const { AdMob } = await getAdMob();
    const adId = pickInterstitialId(Capacitor.getPlatform());
    if (!adId) return false;
    await AdMob.prepareInterstitial({ adId });
    await AdMob.showInterstitial();
    return true;
  } catch (e) {
    console.log('AdMob interstitial error:', e);
    return false;
  }
}
